import * as React from 'react';

import { Button } from '@lightspeed/flame/Button';
import { Flex } from '@lightspeed/flame/Core';
import { Text } from '@lightspeed/flame/Text';
import { Formik } from 'formik';
import Yup from 'yup';

import { translate } from 'pages/utils/translate';
import Monitoring from 'shared_modules/Monitoring';

import { MainToggleSwitch } from './MainToggleSwitch';
import { TipSettingsProvider, useTipSettings } from './store';
import { TipOptionEnum, ITipOptions } from './store/type';
import { TipOptions } from './TipOptions';

// Exported for testing only
// The below messages will change in the future, current ones are placeholder
export const warningMessages = (() => {
  const schemaMessage = (label: string) => ({
    required: translate(`${label} is required`),
    min: translate(`Tip option must be between 1% and 100%`),
    max: translate(`Tip option must be between 1% and 100%`),
  });
  const option1 = schemaMessage('Option 1');
  const option2 = schemaMessage('Option 2');
  const option3 = schemaMessage('Option 3');
  return {
    option1,
    option2,
    option3,
  };
})();

const validationSchema = Yup.object().shape({
  [TipOptionEnum.option1]: Yup.number()
    .required(warningMessages.option1.required)
    .min(1, warningMessages.option1.min)
    .max(100, warningMessages.option1.max),
  [TipOptionEnum.option2]: Yup.number()
    .required(warningMessages.option2.required)
    .min(1, warningMessages.option2.min)
    .max(100, warningMessages.option2.max),
  [TipOptionEnum.option3]: Yup.number()
    .required(warningMessages.option3.required)
    .min(1, warningMessages.option3.min)
    .max(100, warningMessages.option3.max),
});

const Tipping: React.FC = () => {
  const [isLoading, setLoading] = React.useState(false);
  const { tipToggle, tipOptions, updateTipConfiguration } = useTipSettings();

  React.useEffect(() => {
    window.merchantos.asyncSubscribe.notifySubscribers('react.mount.topbar', {
      props: {
        crumbs: [
          {
            type: 'ui_tab',
            text: 'Settings',
            name: 'admin',
          },
          {
            text: 'Tipping',
          },
        ],
      },
    });
  }, []);

  const onSubmit = async (result: ITipOptions) => {
    setLoading(true);
    try {
      await updateTipConfiguration({ tipToggle, tipOptions: result });
    } catch (e) {
      Monitoring.NRError(e, 'ReactViewTipSettings', {
        errorName: 'tip.error.update',
        description: 'Unable to update the tip settings when saving the settings.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={tipOptions}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      render={formikProps => {
        const { dirty, isValid, handleSubmit } = formikProps;

        return (
          <Flex flexDirection="column" px={10} py={6}>
            <Flex justifyContent="space-between" mb={8}>
              <Text fontSize="xlarge" fontWeight="bold">
                {translate(`Tipping`)}
              </Text>
              {tipToggle && (
                <Button
                  aria-label="Save"
                  variant="secondary"
                  fill
                  loading={isLoading}
                  disabled={!dirty || !isValid}
                  onClick={() => handleSubmit()}
                >
                  {translate(`Save`)}
                </Button>
              )}
            </Flex>
            <MainToggleSwitch />
            <TipOptions />
          </Flex>
        );
      }}
    />
  );
};

export const TipWrapper: React.FC = () => {
  return (
    <TipSettingsProvider>
      <Tipping />
    </TipSettingsProvider>
  );
};
